import type { GeoPoint } from './geoPoint';

/**
 * Ported from wear/src/main/java/com/example/myrangefinder/ui/screens/round/geometry/GeoProjection.kt.
 *
 * Equirectangular projection corrected by cos(centerLat), so metres-per-degree match on both
 * axes inside one hole. Optionally rotates the world so a tee→pin vector points up the screen.
 *
 * The Compose types are the only things that changed: Offset → {x, y}, Size → {width, height}.
 * The arithmetic is transcribed line for line, including the Y flip (canvas Y grows down,
 * latitude grows north) — a projection that disagrees with the watch would put the pin in the
 * wrong place on a screen the player is using to judge a shot.
 */

export interface GeoBoundingBox {
  minLongitude: number;
  maxLongitude: number;
  minLatitude: number;
  maxLatitude: number;
}

export interface Point2D {
  x: number;
  y: number;
}

export interface CanvasSize {
  width: number;
  height: number;
}

const toRadians = (deg: number): number => (deg * Math.PI) / 180;

/** Spans fall back to a non-zero epsilon, matching the Kotlin's `takeIf { it > 0 } ?: 1e-6`. */
export function lngSpan(b: GeoBoundingBox): number {
  const span = b.maxLongitude - b.minLongitude;
  return span > 0 ? span : 1e-6;
}

export function latSpan(b: GeoBoundingBox): number {
  const span = b.maxLatitude - b.minLatitude;
  return span > 0 ? span : 1e-6;
}

export function centerLongitude(b: GeoBoundingBox): number {
  return (b.minLongitude + b.maxLongitude) / 2;
}

export function centerLatitude(b: GeoBoundingBox): number {
  return (b.minLatitude + b.maxLatitude) / 2;
}

/** Expands the box by `padFraction` of its size on each side. */
export function paddedBox(b: GeoBoundingBox, padFraction: number): GeoBoundingBox {
  const padLng = lngSpan(b) * padFraction;
  const padLat = latSpan(b) * padFraction;
  return {
    minLongitude: b.minLongitude - padLng,
    maxLongitude: b.maxLongitude + padLng,
    minLatitude: b.minLatitude - padLat,
    maxLatitude: b.maxLatitude + padLat,
  };
}

/** Scales around the centre. factor < 1 zooms in, > 1 zooms out. */
export function scaledBox(b: GeoBoundingBox, factor: number): GeoBoundingBox {
  const halfLng = (lngSpan(b) / 2) * factor;
  const halfLat = (latSpan(b) / 2) * factor;
  const cLng = centerLongitude(b);
  const cLat = centerLatitude(b);
  return {
    minLongitude: cLng - halfLng,
    maxLongitude: cLng + halfLng,
    minLatitude: cLat - halfLat,
    maxLatitude: cLat + halfLat,
  };
}

export interface BuildOptions {
  padding?: number;
  /** Rotates the world so this vector points up the screen. */
  orientFromTo?: { from: GeoPoint; to: GeoPoint };
  panX?: number;
  panY?: number;
}

export class GeoProjection {
  private constructor(
    private readonly bbox: GeoBoundingBox,
    readonly rotationRadians: number,
    readonly scale: number,
    private readonly offsetX: number,
    private readonly offsetY: number,
  ) {}

  static build(
    bbox: GeoBoundingBox,
    canvasSize: CanvasSize,
    { padding = 12, orientFromTo, panX = 0, panY = 0 }: BuildOptions = {},
  ): GeoProjection {
    const latCorrection = Math.cos(toRadians(centerLatitude(bbox)));
    const halfWidthMeters = (lngSpan(bbox) / 2) * latCorrection;
    const halfHeightMeters = latSpan(bbox) / 2;

    // θ = atan2(east, north) is the bearing from north. project() applies a CCW rotation and
    // flips Y when drawing, so rotating by +θ lands the vector pointing up the screen.
    const rotation = orientFromTo
      ? Math.atan2(
          (orientFromTo.to.longitude - orientFromTo.from.longitude) * latCorrection,
          orientFromTo.to.latitude - orientFromTo.from.latitude,
        )
      : 0;

    // A rotated box needs more room than its unrotated extents.
    const sin = Math.abs(Math.sin(rotation));
    const cosA = Math.abs(Math.cos(rotation));
    const rotatedHalfW = halfWidthMeters * cosA + halfHeightMeters * sin;
    const rotatedHalfH = halfWidthMeters * sin + halfHeightMeters * cosA;

    const availableW = Math.max(1, canvasSize.width - 2 * padding);
    const availableH = Math.max(1, canvasSize.height - 2 * padding);

    const scale = Math.min(availableW / (2 * rotatedHalfW), availableH / (2 * rotatedHalfH));

    return new GeoProjection(
      bbox,
      rotation,
      scale,
      canvasSize.width / 2 + panX,
      canvasSize.height / 2 + panY,
    );
  }

  project(point: GeoPoint): Point2D {
    const dx = point.longitude - centerLongitude(this.bbox);
    const dy = point.latitude - centerLatitude(this.bbox);

    const latCorrection = Math.cos(toRadians(centerLatitude(this.bbox)));
    const xMeters = dx * latCorrection;
    const yMeters = dy;

    const sin = Math.sin(this.rotationRadians);
    const cos = Math.cos(this.rotationRadians);
    const xRot = xMeters * cos - yMeters * sin;
    const yRot = xMeters * sin + yMeters * cos;

    // Canvas Y grows downward; latitude grows north. Hence the subtraction.
    return {
      x: this.offsetX + xRot * this.scale,
      y: this.offsetY - yRot * this.scale,
    };
  }

  projectAll(points: GeoPoint[]): Point2D[] {
    return points.map((p) => this.project(p));
  }

  /** Inverse of `project` — canvas pixels back to lat/lng. */
  unproject(canvas: Point2D): GeoPoint {
    const xCanvas = canvas.x - this.offsetX;
    const yCanvas = -(canvas.y - this.offsetY);
    const xRot = xCanvas / this.scale;
    const yRot = yCanvas / this.scale;

    const sin = Math.sin(this.rotationRadians);
    const cosTheta = Math.cos(this.rotationRadians);
    const xMeters = xRot * cosTheta + yRot * sin;
    const yMeters = -xRot * sin + yRot * cosTheta;

    const latCorrection = Math.cos(toRadians(centerLatitude(this.bbox)));
    return {
      longitude: centerLongitude(this.bbox) + xMeters / latCorrection,
      latitude: centerLatitude(this.bbox) + yMeters,
    };
  }
}
